<script setup>
/**
 * 新建公会：提交申请时就选好自己名下的哪个 Agent 当公司 Agent；运营批准后公会、公会金库与公司 Agent 一起生效，申请人成为负责人。
 */
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Badge, Button, Card, Input, Select, Textarea } from '@drama/ui'

import { api, getToken, session } from '../api.js'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const requests = ref(null)
const form = reactive({ name: '', slug: '', description: '', company_agent_id: '' })
const error = ref('')

const REQUEST_BADGE = { pending: 'warning', approved: 'success', rejected: 'danger' }
/** 能当公司 Agent 的：自己名下在岗的 Agent。 */
const candidates = computed(() => session.agents.filter((agent) => agent.status === 'active').map((agent) => ({ value: agent.id, label: agent.full_name })))

async function load() {
  requests.value = (await api('GET', '/api/voko/guild-requests/mine')).create_requests
}

async function submit() {
  error.value = ''
  try {
    await api('POST', '/api/voko/guild-requests', { ...form })
    Object.assign(form, { name: '', slug: '', description: '', company_agent_id: '' })
    await load()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

onMounted(async () => {
  if (getToken() === null) {
    await router.push({ path: '/login', query: { next: route.fullPath } })
    return
  }
  await load()
})
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.guilds') }}</p>
      <h1 class="page-head__title">{{ t('guilds.create.title') }}</h1>
    </div>
  </header>
  <p class="voko-muted">{{ t('guilds.create.intro') }}</p>

  <ul v-if="requests && requests.length > 0" class="voko-guild-requests">
    <li v-for="request in requests" :key="request.id">
      <span>{{ request.name }}（{{ request.slug }}）</span>
      <Badge :variant="REQUEST_BADGE[request.status]" size="sm">{{ t(`guilds.create.status.${request.status}`) }}</Badge>
      <router-link v-if="request.status === 'approved'" :to="`/guilds/${request.slug}`">{{ t('guilds.manage') }}</router-link>
    </li>
  </ul>

  <Card v-if="requests && requests.every((request) => request.status !== 'pending')">
    <p v-if="candidates.length === 0">{{ t('guilds.create.needAgent') }} <router-link to="/agents/new">{{ t('shell.newAgent') }}</router-link></p>
    <form v-else @submit.prevent="submit">
      <label class="voko-field">{{ t('guilds.create.name') }}<Input v-model="form.name" maxlength="120" required /></label>
      <label class="voko-field">{{ t('guilds.create.slug') }}<Input v-model="form.slug" pattern="[a-z0-9][a-z0-9\-]{1,62}" required /></label>
      <label class="voko-field">{{ t('guilds.create.description') }}<Textarea v-model="form.description" :rows="2" /></label>
      <label class="voko-field">{{ t('guilds.create.companyAgent') }}<Select v-model="form.company_agent_id" :options="candidates" :placeholder="t('guilds.create.pickAgent')" /></label>
      <p class="voko-muted">{{ t('guilds.create.companyHint') }}</p>
      <p v-if="error" role="alert">{{ error }}</p>
      <Button type="submit" :disabled="form.name.trim() === '' || form.slug === '' || form.description.trim() === '' || form.company_agent_id === ''">{{ t('guilds.create.submit') }}</Button>
    </form>
  </Card>
</template>

<style scoped>
.voko-guild-requests {
  margin: 0 0 var(--drama-space-md);
  padding: 0;
  list-style: none;
}
.voko-guild-requests li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-xs) 0;
}
</style>
